import React, { useCallback, useRef } from "react"

import { Button, Col, Form, Row } from "react-bootstrap"
import { FaMinus, FaPlus } from "react-icons/fa"
import { toast } from "react-toastify"

import useAuthFormProcessEmail from "../../../api/hooks/useAuthFormProcessEmail"
import OrderResultRecord from "../../../api/OrderResults/OrderResultModel/OrderResultRecord"
import { EmailRegexp } from "../../../config/Constants"
import ActionButton from "../../ActionButton"

export interface EmailItem {
  id: number
  value: string
}

interface EmailFormListProps {
  emailItems: EmailItem[]
  setEmailItems: React.Dispatch<React.SetStateAction<EmailItem[]>>
  details?: OrderResultRecord
}

function EmailFormList({ emailItems, setEmailItems, details = undefined }: EmailFormListProps) {
  const idCounter = useRef(0) // tracks unique IDs for email items. UUID would be better.

  const getNextId = useCallback(() => {
    idCounter.current += 1
    return idCounter.current
  }, [])

  const { processing, sendAuthFormByEmail } = useAuthFormProcessEmail()

  const isEmailValid = useCallback((value: string) => EmailRegexp.test(value), [])

  const canAddEmail = useCallback((value: string) => value.length > 0 && isEmailValid(value), [isEmailValid])

  const addEmail = useCallback(() => {
    setEmailItems((prev) => [...prev, { id: getNextId(), value: "" }])
  }, [getNextId, setEmailItems])

  const removeEmail = useCallback(
    (id: number) => {
      setEmailItems((prev) => prev.filter((item) => item.id !== id))
    },
    [setEmailItems]
  )

  const changeEmail = useCallback(
    (email: string, id: number) => {
      setEmailItems((prev) => prev.map((item) => (item.id === id ? { ...item, value: email } : item)))
    },
    [setEmailItems]
  )

  const handleSendEmail = useCallback(() => {
    const filtered = emailItems.filter(Boolean)

    if (filtered.length === 0) {
      toast.error("Please enter email address.")
      return
    }

    if (new Set(emailItems.map((v) => v.value.toLowerCase())).size !== emailItems.length) {
      toast.error("Duplicate email address is present in the list.")
      return
    }

    sendAuthFormByEmail({
      emailAddress: emailItems.map((v) => v.value),
      formFoxTestAN: Number(details?.FormFoxTestAN || 0),
      personId: details?.PersonID || 0,
      vendorId: details?.CollectionVendorID || 0,
    })
  }, [details, emailItems, sendAuthFormByEmail])

  return (
    <>
      {emailItems.map((item, index) => (
        <Row className="pt-2" key={`email-${item.id}`}>
          <Col xs={12} sm={3} md={3} lg={2}>
            {index === 0 && "Email form:"}
          </Col>
          <Col xs={9} sm={5} md={4}>
            <Form.Control
              size="sm"
              maxLength={40}
              type="text"
              value={item.value}
              onChange={(e) => changeEmail(e.target.value, item.id)}
              readOnly={emailItems.length !== index + 1}
              isInvalid={!!item.value && !isEmailValid(item.value)}
            />
          </Col>
          <Col xs={3} sm={2} md={2}>
            {emailItems.length === index + 1 ? (
              <Button
                size="sm"
                variant="primary"
                className="btn-sm-icon"
                onClick={addEmail}
                disabled={!canAddEmail(item.value)}
              >
                <FaPlus />
              </Button>
            ) : (
              <Button size="sm" variant="primary" className="btn-sm-icon" onClick={() => removeEmail(item.id)}>
                <FaMinus />
              </Button>
            )}
          </Col>
          {index === 0 && (
            <Col xs={3} sm={2} md={2}>
              <ActionButton
                size="sm"
                className="fixed-w10"
                onClick={handleSendEmail}
                disabled={emailItems.some((email) => !isEmailValid(email.value))}
                spin={processing}
              >
                Send Email
              </ActionButton>
            </Col>
          )}
        </Row>
      ))}
    </>
  )
}

export default EmailFormList
